<template>
  <mp-drawer :is-open="isOpen" :on-close="close" scroll-behavior="inside" close-on-overlay-click>
    <mp-drawer-content max-w="448px">
      <mp-drawer-header>{{ isEdit ? 'Edit group' : 'Create new group' }}</mp-drawer-header>
      <mp-drawer-close-button :is-disabled="isLoadingSubmit" />
      <mp-drawer-body>
        <mp-box v-if="isLoadingDetail" display="flex" align-items="center" justify-content="center" min-h="300px">
          <mp-spinner size="lg" color="primary.500" />
        </mp-box>
        <mp-flex v-else flex-direction="column" gap="8">
          <!-- Group name -->
          <mp-form-control :is-invalid="isNameInvalid" w="280px">
            <mp-flex justify-content="space-between" align-items="center">
              <mp-form-label>Group name</mp-form-label>
              <mp-text font-size="sm" color="gray.400">
                {{ `${form.name ? form.name.length : 0}/${MAX_GROUP_NAME}` }}
              </mp-text>
            </mp-flex>
            <mp-input v-model="form.name" is-full-width :maxlength="MAX_GROUP_NAME" @blur="$v.form.name.$touch" @input="errorName = ''" />
            <mp-form-error-message>
              <mp-box v-if="isNameInvalid" data-element="error-message">
                {{ errorNameMessage }}
              </mp-box>
            </mp-form-error-message>
          </mp-form-control>

          <!-- Contributor scope -->
          <mp-flex flex-direction="column" gap="4">
            <mp-box>
              <mp-text font-weight="semibold" font-size="lg">Contributor scope</mp-text>
              <mp-text color="gray.600" font-size="sm">Define which employees that contributor can review.</mp-text>
            </mp-box>

            <mp-box>
              <mp-checkbox v-model="form.is_apply_all_scopes" mb="0">
                Apply all scope for the contributor in this group
              </mp-checkbox>
            </mp-box>

            <info
              v-if="form.is_apply_all_scopes"
              description="This group contributor can calibrate all employee review results in this calibration across all job positions, organizations, job levels and branches."
            />

            <mp-box v-else bg="gray.25" rounded="md">
              <mp-box :text-align="!hasAddedScopes ? 'center' : ''" p="4">
                <!-- No scope have been set yet -->
                <mp-box v-if="!hasAddedScopes">
                  <mp-text font-weight="semibold">No scope have been set yet</mp-text>
                  <mp-text color="gray.600" font-size="sm">This group scope will be displayed here.</mp-text>
                </mp-box>

                <mp-box v-else>
                  <input-scope
                    v-for="scope in addedScopes"
                    :key="`input-${scope}`"
                    v-model="form.scopes[`${scope}_ids`]"
                    :label="scopeOptions[scope].label"
                    :items="optionsData[scope]"
                    :is-validating="$v.form.scopes.$error && !$v.form.scopes.allAddedScopesHaveValues"
                    @remove="removeScope(scope)"
                  />
                </mp-box>

                <pixel-popover trigger-variant="outline" trigger-size="md" dropdown-width="180px">
                  <template #popover-trigger>
                    <mp-button :is-disabled="isLoadingScopes" :is-loading="isLoadingScopes" variant="outline" mt="4" data-qa="btn-add-scope">
                      <mp-icon name="add" mr="2" />
                      Add scope
                    </mp-button>
                  </template>
                  <template #popover-content>
                    <mp-popover-list>
                      <mp-popover-list-item
                        v-for="(scope, key) in scopeOptions"
                        :key="key"
                        :is-disabled="scope.is_added"
                        :data-qa="`option-scope-${key}`"
                        @click="addScope(key)"
                      >
                        {{ scope.label }}
                      </mp-popover-list-item>
                    </mp-popover-list>
                  </template>
                </pixel-popover>

                <mp-box v-if="$v.form.scopes.$error" color="red.400" font-size="sm" mt="2" data-element="error-message">
                  <template v-if="!$v.form.scopes.atLeastOneScope && !hasAddedScopes">Contributor scope must be selected.</template>
                </mp-box>
              </mp-box>
            </mp-box>
          </mp-flex>

          <!-- Contributors -->
          <mp-flex flex-direction="column" gap="4">
            <mp-box>
              <mp-text font-weight="semibold" font-size="lg">Contributors</mp-text>
              <mp-text color="gray.600" font-size="sm">
                Add the contributor who can calibrate the employee review result based on the defined scope above.
              </mp-text>
            </mp-box>

            <mp-box>
              <mp-button variant="outline" data-qa="btn-add-contributor" @click="isModalAddContributor = true">
                <mp-icon name="add" mr="2" />
                Add contributor
              </mp-button>

              <mp-box
                v-if="$v.form.contributors.$error && !$v.form.contributors.required"
                color="red.400"
                font-size="sm"
                mt="2"
                data-element="error-message"
                >Contributor must be assigned.</mp-box
              >

              <contributor-list :contributors="form.contributors" @remove="removeContributor" />
            </mp-box>
          </mp-flex>
        </mp-flex>

        <modal-add-member
          title="Assign contributor"
          subtitle="Which employee would you like to assign for calibration contributor?"
          search-placeholder="Search employee name"
          :url="`${$newEndPoint}/calibration/list-users`"
          :is-open.sync="isModalAddContributor"
          :selected-member="form.contributors"
          :max-select="50"
          @save="onSaveContributor"
        />
      </mp-drawer-body>
      <mp-drawer-footer>
        <mp-flex justify-content="space-between" align-items="center" w="full">
          <mp-button variant="link" color="gray.600" :is-disabled="isLoadingSubmit || isLoadingDetail" data-qa="btn-reset" @click="resetForm"
            >Reset</mp-button
          >
          <mp-flex gap="3">
            <mp-button :is-disabled="isLoadingSubmit || isLoadingDetail" variant="ghost" data-qa="btn-cancel" @click="close">Cancel</mp-button>
            <mp-button :is-disabled="isLoadingSubmit || isLoadingDetail" :is-loading="isLoadingSubmit" data-qa="btn-submit" @click="submit"
              >Submit</mp-button
            >
          </mp-flex>
        </mp-flex>
      </mp-drawer-footer>
    </mp-drawer-content>

    <mp-drawer-overlay />
  </mp-drawer>
</template>
<script>
import { maxLength, required } from 'vuelidate/lib/validators';
import { alertMixin } from '@/mixins/general';
import roleMixin from '@/mixins/role';
import Info from '@/components/Info';
import PixelPopover from '@/components/pixel/Popover';
import InputScope from '@/views/reviews/calibration/master-group/InputScope';
import ModalAddMember from '@/components/modals/ModalAddMember';
import ContributorList from '@/components/ContributorList';

const MAX_GROUP_NAME = 60;

export default {
  name: 'DrawerCalibrationGroupForm',
  components: { Info, PixelPopover, InputScope, ModalAddMember, ContributorList },
  mixins: [alertMixin, roleMixin],
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    uuid: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      MAX_GROUP_NAME,
      isLoadingScopes: false,
      isLoadingSubmit: false,
      isLoadingDetail: false,
      isEdit: false,
      scopeOptions: {
        branch: { label: 'Branch', is_added: false },
        job_level: { label: 'Job level', is_added: false },
        job_position: { label: 'Job position', is_added: false },
        organization: { label: 'Organization', is_added: false },
      },
      addedScopes: [], // ['branch', etc] To display scopes by latest added
      form: {
        name: '',
        is_apply_all_scopes: false,
        scopes: {
          organization_ids: [],
          branch_ids: [],
          job_position_ids: [],
          job_level_ids: [],
        },
        contributors: [],
      },
      isModalAddContributor: false,
      errorName: '',
    };
  },
  validations: {
    form: {
      name: { required, maxLength: maxLength(MAX_GROUP_NAME) },
      scopes: {
        allAddedScopesHaveValues(value) {
          if (this.form.is_apply_all_scopes) return true;
          // Check if all added scopes have at least one selected value
          return Object.keys(this.scopeOptions).every(key => {
            if (this.scopeOptions[key].is_added) return value[`${key}_ids`]?.length > 0;
            return true; // If not added, validation passes
          });
        },
        atLeastOneScope(value) {
          if (this.form.is_apply_all_scopes) return true;
          return (
            value.organization_ids?.length > 0 ||
            value.branch_ids?.length > 0 ||
            value.job_position_ids?.length > 0 ||
            value.job_level_ids?.length > 0
          );
        },
      },
      contributors: { required },
    },
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.resetForm();
        if (this.uuid) {
          this.isEdit = true;
          this.fetchGroupDetail(this.uuid);
        } else {
          this.isEdit = false;
        }
      }
    },
  },
  computed: {
    hasAddedScopes() {
      return Object.values(this.scopeOptions).some(scope => scope.is_added);
    },
    optionsData() {
      const filterData = this.$store.getters.filterByCompanyId;
      return {
        organization: filterData?.organizations ?? [],
        branch: filterData?.branches ?? [],
        job_position: filterData?.jobPositions ?? [],
        job_level: filterData?.jobLevels ?? [],
      };
    },
    errorNameMessage() {
      if (this.errorName) return this.errorName;
      if (!this.$v.form.name.maxLength) return `Group name must have at most ${this.MAX_GROUP_NAME} characters.`;
      return 'Group name must be filled.';
    },
    isNameInvalid() {
      return Boolean(this.$v.form.name.$error || this.errorName);
    },
  },
  async mounted() {
    this.fetchFilterOptions();
  },
  methods: {
    async fetchFilterOptions() {
      this.isLoadingScopes = true;
      await this.$store
        .dispatch('getFilterOptionsByCompanyId', this.cid)
        .then(() => {
          this.isLoadingScopes = false;
        })
        .catch(error => {
          console.error('Failed to fetch filter options:', error);
          this.isLoadingScopes = false;
        });
    },

    async fetchGroupDetail(uuid) {
      this.isLoadingDetail = true;
      try {
        const url = `${this.$newEndPoint}/calibration/groups/${uuid}`;
        const { data, response } = await this.$axios.get(url);

        if (data?.status_code === 200 && data?.contents) {
          const group = data.contents;
          this.form.name = group.name || '';
          this.form.is_apply_all_scopes = group.is_apply_all_scopes || false;
          // Reset scope options and addedScopes
          Object.keys(this.scopeOptions).forEach(key => {
            this.scopeOptions[key].is_added = false;
          });
          this.addedScopes = [];
          if (group.scopes) {
            Object.keys(group.scopes).forEach(key => {
              if (Array.isArray(group.scopes[key]) && group.scopes[key].length > 0) {
                // response from API is Object {id, name}, we need only id as string
                this.form.scopes[key] = group.scopes[key].map(scope => scope.id.toString());
                const scopeType = key.replace('_ids', '');
                if (this.scopeOptions[scopeType]) {
                  this.scopeOptions[scopeType].is_added = true;
                  this.addedScopes.push(scopeType);
                }
              } else {
                this.form.scopes[key] = [];
              }
            });
          } else {
            // If is_apply_all_scopes, clear all scopes
            Object.keys(this.form.scopes).forEach(key => {
              this.form.scopes[key] = [];
            });
          }
          this.form.contributors = Array.isArray(group.contributors) ? group.contributors : [];
        } else {
          throw Error(response?.data);
        }
      } catch (e) {
        this.alertToast(e?.message || 'Failed to fetch group detail', 'danger');
        this.close();
      } finally {
        this.isLoadingDetail = false;
      }
    },
    onSaveContributor(val) {
      this.form.contributors = val;
      this.isModalAddContributor = false;
    },
    removeContributor(id) {
      this.form.contributors = this.form.contributors.filter(contributor => contributor.id !== id);
    },
    addScope(type) {
      if (this.scopeOptions[type]) {
        this.$v.form.scopes.$reset();
        this.addedScopes.push(type);
        this.scopeOptions[type].is_added = true;
      }
    },
    removeScope(type) {
      if (this.scopeOptions[type]) {
        this.addedScopes = this.addedScopes.filter(scope => scope !== type);
        this.scopeOptions[type].is_added = false;
        this.form.scopes[`${type}_ids`] = [];
      }
    },
    close() {
      this.$emit('update:isOpen', false);
    },
    resetForm() {
      this.errorName = '';

      // Reset scope is_added flags
      Object.keys(this.scopeOptions).forEach(key => {
        this.scopeOptions[key].is_added = false;
      });

      this.form = {
        name: '',
        is_apply_all_scopes: false,
        scopes: {
          organization_ids: [],
          branch_ids: [],
          job_position_ids: [],
          job_level_ids: [],
        },
        contributors: [],
      };
      this.addedScopes = [];
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.post();
      } else {
        this.$nextTick(() => {
          setTimeout(() => {
            const errorElement = document.querySelector('[data-element="error-message"]');
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 300); // Wait for accordion animation to complete
        });
      }
    },
    async post() {
      try {
        this.isLoadingSubmit = true;
        const payload = {
          name: (this.form.name || '').trim(),
          is_apply_all_scopes: this.form.is_apply_all_scopes,
          scopes: this.form.is_apply_all_scopes
            ? null
            : {
                organization_ids: this.form.scopes.organization_ids.map(Number),
                branch_ids: this.form.scopes.branch_ids.map(Number),
                job_position_ids: this.form.scopes.job_position_ids.map(Number),
                job_level_ids: this.form.scopes.job_level_ids.map(Number),
              },
          contributor_ids: this.form.contributors.map(contributor => contributor.id),
        };
        let data;
        let response;
        if (this.isEdit && this.uuid) {
          const url = `${this.$newEndPoint}/calibration/groups/${this.uuid}`;
          ({ data, response } = await this.$axios.put(url, payload));
        } else {
          const url = `${this.$newEndPoint}/calibration/groups`;
          ({ data, response } = await this.$axios.post(url, payload));
        }

        if (data?.status_code === 200) {
          this.alertToast(this.isEdit ? 'Group edited' : 'Group created');
          this.$emit('updated');
          this.close();
        } else {
          if (response?.status === 422 && response?.data?.message.includes('already exists')) {
            this.errorName = 'Group name already exists. Please use a different name.';
          }
          throw Error(response?.data?.message);
        }
      } catch (e) {
        this.alertToast(e?.message || `Failed to ${this.isEdit ? 'edit' : 'create'} group`, 'danger');
      } finally {
        this.isLoadingSubmit = false;
      }
    },
  },
};
</script>
